import React, { useRef, useEffect, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import classnames from 'classnames';
import __ from 'utils/i18n';
import Loader from 'components/Loader';
import { DateCell } from './DateCell';
import { ConceptCell } from './ConceptCell';
import { AmountCell } from './AmountCell';
import { PartialBalanceCell } from './PartialBalanceCell';
import './MovementsTable.scss';

const COLUMNS = [
  {
    id: 'date',
    header: 'dashboard.sections.movements.columns.date',
    Cell: DateCell,
    width: '20%',
  },
  {
    id: 'concept',
    header: 'dashboard.sections.movements.columns.concept',
    Cell: ConceptCell,
    width: '30%',
  },
  {
    id: 'amount',
    header: 'dashboard.sections.movements.columns.amount',
    Cell: AmountCell,
    width: '25%',
  },
  {
    id: 'partialBalance',
    header: 'dashboard.sections.movements.columns.partialBalance',
    Cell: PartialBalanceCell,
    width: '25%',
  },
];

const MovementRow = ({ item, virtualRow, onMouseMove, onMouseLeave }) => {
  const hasDescription = item?.description && item.description.trim() !== '';
  const tooltipText = hasDescription ? item.description : 'Sin descripción';
  
  // Basarse en el signo del monto para determinar si es entrada o salida
  const isMoneyIn = item?.amount?.amount > 0;

  return (
    <div
      className={classnames('movements-table-row', {
        'row-credit': isMoneyIn,
        'row-debit': !isMoneyIn,
        'has-description': hasDescription,
      })}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: `${virtualRow.size}px`,
        transform: `translateY(${virtualRow.start}px)`,
        cursor: 'default',
      }}
      onMouseMove={(e) => onMouseMove(e, tooltipText)}
      onMouseLeave={onMouseLeave}
    >
      {COLUMNS.map((column) => (
        <div
          key={column.id}
          className="movements-table-cell"
          style={{ width: column.width }}
        >
          <column.Cell cell={{ original: item }} />
        </div>
      ))}
    </div>
  );
};

export const MovementsTable = ({
  data = [],
  isFetching,
  hasMore,
  getMore,
  error,
}) => {
  const parentRef = useRef(null);
  const [tooltip, setTooltip] = useState({ show: false, text: '', x: 0, y: 0 });

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 56, // Altura estimada de cada fila en px
    overscan: 10, // Cantidad de filas a renderizar fuera del viewport
  });

  // Detectar cuando se llega al final para cargar más
  const virtualItems = virtualizer.getVirtualItems();
  
  useEffect(() => {
    if (virtualItems.length === 0) return;

    const lastItem = virtualItems[virtualItems.length - 1];
    
    if (
      lastItem &&
      lastItem.index >= data.length - 5 && // Cargar cuando faltan 5 items
      hasMore &&
      !isFetching &&
      !error
    ) {
      getMore();
    }
  }, [virtualItems, data.length, hasMore, isFetching, error, getMore]);

  const handleMouseMove = (e, text) => {
    if (!parentRef.current) return;
    
    const rect = parentRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setTooltip({
      show: true,
      text,
      x,
      y,
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, text: '', x: 0, y: 0 });
  };

  if (data.length === 0 && !isFetching) {
    return (
      <div className="movements-empty">
        <p>{__('dashboard.sections.movements.empty')}</p>
      </div>
    );
  }

  return (
    <div className="movements-table-container">
      {/* Header */}
      <div className="movements-table-header">
        {COLUMNS.map((column) => (
          <div
            key={column.id}
            className="movements-table-header-cell"
            style={{ width: column.width }}
          >
            {__(column.header)}
          </div>
        ))}
      </div>

      {/* Body con virtual scroll */}
      <div
        ref={parentRef}
        className="movements-table-body"
        style={{ height: '500px', overflowY: 'auto', position: 'relative' }}
      >
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualItems.map((virtualRow) => {
            const item = data[virtualRow.index];
            return (
              <MovementRow
                key={virtualRow.key}
                item={item}
                virtualRow={virtualRow}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              />
            );
          })}
        </div>
        
        {/* Tooltip flotante que sigue el cursor */}
        {tooltip.show && (
          <div
            className="floating-tooltip"
            style={{
              left: `${tooltip.x + 15}px`,
              top: `${tooltip.y + 15}px`,
            }}
          >
            {tooltip.text}
          </div>
        )}
      </div>

      {/* Loader */}
      {isFetching && (
        <div className="movements-loader">
          <Loader />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="movements-error">
          <p>{__('dashboard.sections.movements.error')}</p>
        </div>
      )}
    </div>
  );
};

export default MovementsTable;

